import { useCallback } from 'react'
import { gql, QueryResult, useApolloClient, useQuery, useMutation } from '@apollo/client'
import {
  CreatePaypalPurchaseMutation,
  CreatePaypalPurchaseMutationVariables,
  PaypalPaymentInput,
  GuestPaypalPaymentInput,
  CreateGuestPaypalPurchaseMutationVariables,
  CreateGuestPaypalPurchaseMutation,
} from '@/types/codegen-federation'

const getOrdersAndMarketSubscriptions = gql`
  query getOrdersAndMarketSubscriptions {
    orders {
      created
      currency
      id
      project {
        slug
        title
      }
      price
      status
      subscriptionrecordSet {
        id
      }
    }
    marketSubscriptions {
      CanceledAt
      created
      currency
      end
      id
      interval
      name
      offerSnapshot
      price
      platformSubscriptionId
      project {
        slug
        title
      }
      status
    }
  }
`

export const cancelSubscription = gql`
  mutation stripeSubscriptionCancel($input: CancelStripeSubscriptionInput!) {
    stripeSubscriptionCancel(input: $input) {
      subscription {
        id
      }
    }
  }
`

export const newSubscription = gql`
  mutation stripeSubscription($input: StripePaymentInput!) {
    stripeSubscription(input: $input) {
      subscription {
        id
      }
    }
  }
`

export const CREATE_GUEST_PAYPAL_PURCHASE = gql`
  mutation createGuestPaypalPurchase($input: GuestPaypalPaymentInput!) {
    guestPaypalPurchase(input: $input) {
      order {
        id
      }
    }
  }
`

export const CREATE_PAYPAL_PURCHASE = gql`
  mutation createPaypalPurchase($input: PaypalPaymentInput!) {
    paypalPurchase(input: $input) {
      order {
        id
      }
    }
  }
`

export const useCreateGuestPaypalPurchase = () => {
  const [mutate] = useMutation<CreateGuestPaypalPurchaseMutation, CreateGuestPaypalPurchaseMutationVariables>(
    CREATE_GUEST_PAYPAL_PURCHASE,
  )

  const createGuestPaypalPurchase = useCallback(
    (input: GuestPaypalPaymentInput) => {
      mutate({ variables: { input } })
    },
    [mutate],
  )

  return {
    createGuestPaypalPurchase,
  }
}

export const useCreatePaypalPurchase = () => {
  const [mutate] = useMutation<CreatePaypalPurchaseMutation, CreatePaypalPurchaseMutationVariables>(
    CREATE_PAYPAL_PURCHASE,
  )

  const createPaypalPurchase = useCallback(
    (input: PaypalPaymentInput) => {
      mutate({ variables: { input } })
    },
    [mutate],
  )

  return {
    createPaypalPurchase,
  }
}

export interface OrdersAndSubscriptions {
  orders: Order[]
  marketSubscriptions: Subscription[]
}

export interface Order {
  created: string
  currency: string
  id: number
  project: {
    slug: string
    title: string
  }
  price: number
  status: PaymentStatus
  subscriptionrecordSet: [{ id: number }] | []
}

export type SubscriptionInterval = 'month' | 'year'

export interface Subscription {
  CanceledAt: string | null
  created: string
  currency: string
  end: string
  id: string
  interval: SubscriptionInterval
  name: string
  offerSnapshot: string
  price: number
  project: { slug: string; title: string }
  status: PaymentStatus
  platformSubscriptionId: string
}

export interface Perk {
  id: string
  name: string
  product: {
    active: boolean
    name: string
    id: string
  }
}

export enum OrderType {
  OneTime = 'One Time',
  Subscription = 'Subscription Renewal',
}

export enum PaymentStatus {
  Canceled = 'canceling',
  Completed = 'completed',
  Incomplete = 'incomplete',
  Active = 'active',
}

export const useOrdersAndSubscriptions = (): QueryResult<OrdersAndSubscriptions> => {
  const client = useApolloClient()
  return useQuery(getOrdersAndMarketSubscriptions, {
    client,
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'network-only',
    refetchWritePolicy: 'overwrite',
  })
}
